import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { isSameDay } from "date-fns";
import toast from "react-hot-toast";
import { 
    Box,
    CircularProgress, 
    Alert 
} from "@mui/material";
import api from "state/axios";
import db from "state/db";
import useProductCache from "../hooks/useProductCache";
import { fetchLegMembersByFlt } from "state/api";
import ProductCatalog from "./ProductCatalog";
import FlightList from "./FlightList";
import FlightDetailDialog from "./FlightDetailDialog";


const flightKey = (f) => `${f.flightDate}|${f.flightDesc}|${f.dep}`;

const newObjectId = () => {
    const ts = Math.floor(Date.now() / 1000).toString(16).padStart(8, "0");
    const rand = Array.from(crypto.getRandomValues(new Uint8Array(8)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    return ts + rand;
};

const SessionSetup = ({ session, onSessionChange, isOnline, loading }) => {
    const user        = useSelector((state) => state.auth.user);
    const crewFlights = useSelector((state) => state.auth.crewFlights);
    const crewRanks   = useSelector((state) => state.auth.crewRanks);

    const { refreshCache, loading: cacheLoading, lastCached } = useProductCache();

    const [cachedCount, setCachedCount] = useState(0);
    const [products, setProducts]       = useState([]);
    const [submitting, setSubmitting]   = useState(false);

    const [sessions, setSessions]               = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);

    const [detailOpen, setDetailOpen]       = useState(false);
    const [detailFlight, setDetailFlight]   = useState(null);
    const [detailSession, setDetailSession] = useState(null);

    const [inventoryRows, setInventoryRows] = useState([]);
    const [editMode, setEditMode]           = useState(false);
    const [editRows, setEditRows]           = useState([]);
    const [crewEditMode, setCrewEditMode]   = useState(false);
    const [crewRows, setCrewRows]           = useState([]);
    const [availableCrew, setAvailableCrew] = useState([]);
    const [syncingSession, setSyncingSession] = useState(false);

    // ── Load products from Dexie ──
    useEffect(() => {
        db.products.count().then(setCachedCount);
        db.products.toArray().then(setProducts);
    }, [lastCached]);

    // ── Auto-download catalog on first use ──
    useEffect(() => {
        if (!isOnline || cacheLoading) return;
        db.products.count().then((count) => {
            if (count === 0) refreshCache().catch(() => {});
        });
    }, [isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Crew cache helpers ──
    const getFlightCrew = useCallback(async (flight) => {
        const key = flightKey(flight);
        try {
            const cached = await db.flightCrew.get(key);
            if (cached?.crew?.length > 0) return cached.crew;
        } catch {}

        const cabinCodes = new Set(
            crewRanks.filter((r) => r.rankType === "Cabin Crew").map((r) => r.rankCode)
        );
        const { legMembers } = await fetchLegMembersByFlt(flight.flightDate, flight.flightDesc, flight.dep);
        const crew = legMembers
            .filter((lm) => cabinCodes.has(lm.post) && !lm.pax)
            .map((lm) => ({ crewCode: lm.crewCode, crewName: lm.crewName, post: lm.post || "", base: lm.base || "" }));
        if (crew.length > 0) {
            await db.flightCrew.put({ flightKey: key, crew, fetchedAt: new Date().toISOString() });
        }
        return crew;
    }, [crewRanks]);

    // Pre-fetch crew for all flights in background when online
    useEffect(() => {
        if (!isOnline || !crewFlights?.length) return;
        crewFlights.forEach(async (flight) => {
            const key = flightKey(flight);
            try {
                const cached = await db.flightCrew.get(key);
                if (cached?.crew?.length > 0) return;
                const cabinCodes = new Set(
                    crewRanks.filter((r) => r.rankType === "Cabin Crew").map((r) => r.rankCode)
                );
                const { legMembers } = await fetchLegMembersByFlt(flight.flightDate, flight.flightDesc, flight.dep);
                const crew = legMembers
                    .filter((lm) => cabinCodes.has(lm.post) && !lm.pax)
                    .map((lm) => ({ crewCode: lm.crewCode, crewName: lm.crewName, post: lm.post || "", base: lm.base || "" }));
                if (crew.length > 0) {
                    await db.flightCrew.put({ flightKey: key, crew, fetchedAt: new Date().toISOString() });
                }
            } catch {}
        });
    }, [isOnline, crewFlights]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Load sessions ──
    const loadSessions = useCallback(async () => {
        setSessionsLoading(true);
        if (isOnline) {
            try {
                const res = await api.get("/inflight/sessions");
                const serverSessions = Array.isArray(res.data) ? res.data : [];
                const existing = await db.sessions.bulkGet(serverSessions.map((s) => s._id));
                const PENDING = ["pending_create", "pending_close", "pending_reopen"];
                const toUpsert = serverSessions.filter((_, i) => {
                    const local = existing[i];
                    return !local || !PENDING.includes(local.syncStatus);
                });
                if (toUpsert.length) {
                    await db.sessions.bulkPut(toUpsert.map((s) => ({ ...s, syncStatus: "synced" })));
                }
            } catch {}
        }
        const all = await db.sessions.toArray();
        setSessions(all.filter((s) => !s.deletedAt));
        setSessionsLoading(false);
    }, [isOnline]);

    useEffect(() => { loadSessions(); }, [loadSessions]);

    const getFlightSession = useCallback((flight) =>
        sessions.find((s) =>
            s.flightDesc === flight.flightDesc &&
            s.dep === flight.dep &&
            isSameDay(new Date(s.flightDate), new Date(flight.flightDate))
        ),
    [sessions]);

    const openDetail = (flight) => {
        setDetailFlight(flight);
        setDetailSession(getFlightSession(flight) || null);
        setInventoryRows([]);
        setEditMode(false);
        setCrewEditMode(false);
        setDetailOpen(true);
    };

    // ── Product catalog ──
    const handleDownloadCatalog = async () => {
        try {
            await refreshCache();
            const count = await db.products.count();
            setCachedCount(count);
            db.products.toArray().then(setProducts);
            toast.success(`Downloaded ${count} products.`);
        } catch {
            toast.error("Failed to download catalog.");
        }
    };

    // ── Inventory row updaters ──
    const makeRowUpdater = (setter) => (idx, field, value) =>
        setter((prev) => prev.map((row, i) => {
            if (i !== idx) return row;
            if (field === "productId") {
                const p = products.find((p) => p._id === value);
                return p
                    ? { ...row, productId: p._id, productCode: p.productCode, productName: p.productName,
                        unit: p.unit, category: p.category, unitPrice: p.price ?? 0,
                        commissionRate: p.directCommissionRate ?? 0 }
                    : { ...row, productId: value };
            }
            return { ...row, [field]: value };
        }));

    const updateInventoryRow = makeRowUpdater(setInventoryRows);
    const updateEditRow      = makeRowUpdater(setEditRows);
    const updateCrewRow      = (idx, field, value) =>
        setCrewRows((prev) => prev.map((row, i) => i !== idx ? row : { ...row, [field]: value }));

    // ── Start session ──
    const handleStartSession = async () => {
        if (!detailFlight) return;
        setSubmitting(true);
        try {
            let crewMembers = [];
            try {
                const cached = await getFlightCrew(detailFlight);
                crewMembers = cached.length > 0
                    ? cached
                    : [{ crewCode: user?.crewCode, crewName: user?.crewName, post: user?.post || "", base: user?.base || "" }];
            } catch {
                crewMembers = [{ crewCode: user?.crewCode, crewName: user?.crewName, post: user?.post || "", base: user?.base || "" }];
            }

            const payload = {
                flightDate: detailFlight.flightDate,
                flightActualDayDep: detailFlight.flightActualDayDep,
                flightActualDayArr: detailFlight.flightActualDayArr,
                acType: detailFlight.acType, flightReg: detailFlight.flightReg,
                flightType: detailFlight.flightType, flightDesc: detailFlight.flightDesc,
                flightCarrier: detailFlight.flightCarrier, flightNo: detailFlight.flightNo,
                dep: detailFlight.dep, arr: detailFlight.arr,
                std: detailFlight.std, sta: detailFlight.sta,
                etd: detailFlight.etd, eta: detailFlight.eta,
                crewName: user?.crewName,
                inventory: inventoryRows.filter((r) => r.productId && r.allocatedQty > 0),
                crewMembers,
            };

            const existingUnsynced = await db.sessions
                .filter((s) => s.status === "active" && s.syncStatus !== "synced" && !s.deletedAt)
                .first();
            if (existingUnsynced) {
                toast.error(`Close session ${existingUnsynced.flightDesc} ${existingUnsynced.dep}-${existingUnsynced.arr} before opening a new one.`);
                return;
            }

            if (!isOnline) {
                const existingActive = await db.sessions
                    .filter((s) => s.status === "active" && !s.deletedAt)
                    .first();
                if (existingActive) {
                    toast.error(`Close session ${existingActive.flightDesc} ${existingActive.dep}-${existingActive.arr} before opening a new one.`);
                    return;
                }
                const localId = newObjectId();
                const localSession = {
                    _id: localId, ...payload,
                    crewCode: user?.crewCode, status: "active",
                    openedAt: new Date().toISOString(), closedAt: null,
                    syncStatus: "pending_create",
                };
                await db.sessions.put(localSession);
                await loadSessions();
                onSessionChange(localSession);
                setDetailSession(localSession);
                toast.success("Session saved offline. Will sync when online.");
                return;
            }

            const res = await api.post("/inflight/sessions", payload);
            await db.sessions.put({ ...res.data, syncStatus: "synced" });
            onSessionChange(res.data);
            setDetailSession(res.data);
            await loadSessions();
            toast.success("Session started.");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to start session!");
        } finally {
            setSubmitting(false);
        }
    };

    // ── Edit inventory ──
    const handleOpenEdit = () => {
        setEditRows(
            (detailSession.inventory || []).map((inv) => ({
                productId: inv.productId?.toString() || "", productCode: inv.productCode || "",
                productName: inv.productName || "", unit: inv.unit || "",
                category: inv.category || "", unitPrice: inv.unitPrice ?? 0,
                commissionRate: inv.commissionRate ?? 0, allocatedQty: inv.allocatedQty ?? 1,
            }))
        );
        setEditMode(true);
    };

    const handleSaveInventory = async () => {
        setSubmitting(true);
        try {
            const res = await api.patch(`/inflight/sessions/${detailSession._id}/inventory`, {
                inventory: editRows.filter((r) => r.productId && r.allocatedQty > 0),
            });
            await db.sessions.put({ ...res.data, syncStatus: "synced" });
            setDetailSession(res.data);
            if (session?._id === res.data._id) onSessionChange(res.data);
            await loadSessions();
            setEditMode(false);
            toast.success("Inventory updated.");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to update inventory!");
        } finally {
            setSubmitting(false);
        }
    };

    // ── Crew edit ──
    const handleOpenCrewEdit = async () => {
        const existing = detailSession?.crewMembers || [];
        if (existing.length > 0) {
            setCrewRows(existing.filter((m) => m.crewCode || m.crewName).map((m) => ({ ...m })));
            setCrewEditMode(true);
            try { setAvailableCrew(await getFlightCrew(detailFlight)); } catch { setAvailableCrew([]); }
            return;
        }
        setCrewEditMode(true);
        setCrewRows([]);
        try {
            const crew = await getFlightCrew(detailFlight);
            setAvailableCrew(crew);
            setCrewRows(crew);
        } catch {
            setAvailableCrew([]);
        }
    };

    const handleSaveCrew = async () => {
        setSubmitting(true);
        try {
            const res = await api.patch(`/inflight/sessions/${detailSession._id}/crew`, {
                crewMembers: crewRows.filter((r) => r.crewCode || r.crewName),
            });
            await db.sessions.put({ ...res.data, syncStatus: "synced" });
            setDetailSession(res.data);
            if (session?._id === res.data._id) onSessionChange(res.data);
            await loadSessions();
            setCrewEditMode(false);
            toast.success("Crew list updated.");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to update crew!");
        } finally {
            setSubmitting(false);
        }
    };

    // ── Reopen session ──
    const handleReopenSession = async () => {
        setSubmitting(true);
        try {
            if (!isOnline || detailSession.syncStatus !== "synced") {
                const nextStatus = detailSession.syncStatus === "pending_close" ? "synced" : "pending_reopen";
                const updated = { ...detailSession, status: "active", closedAt: null, syncStatus: nextStatus };
                await db.sessions.put(updated);
                await loadSessions();
                onSessionChange(updated);
                setDetailSession(updated);
                toast.success("Session reopened offline. Will sync when online.");
                return;
            }
            try {
                const res = await api.patch(`/inflight/sessions/${detailSession._id}/reopen`);
                await db.sessions.put({ ...res.data, syncStatus: "synced" });
                onSessionChange(res.data);
                setDetailSession(res.data);
                await loadSessions();
                toast.success("Session reopened.");
            } catch (err) {
                if (err.response?.status === 404) {
                    const { syncStatus, status, closedAt, ...payload } = detailSession;
                    try {
                        const res = await api.post("/inflight/sessions", { ...payload, status: "active", closedAt: null });
                        await db.sessions.put({ ...res.data, syncStatus: "synced" });
                        onSessionChange(res.data);
                        setDetailSession(res.data);
                        await loadSessions();
                        toast.success("Session reopened.");
                    } catch (postErr) {
                        toast.error(
                            postErr.response?.status === 409
                                ? postErr.response.data?.message || "Another active session exists on server."
                                : postErr?.response?.data?.message || "Failed to reopen session!"
                        );
                    }
                } else {
                    toast.error(err?.response?.data?.message || "Failed to reopen session!");
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    // ── Sync session ──
    const handleSyncSession = useCallback(async () => {
        if (!detailSession || !isOnline || syncingSession) return;
        setSyncingSession(true);
        try {
            const sess = detailSession;
            if (sess.syncStatus === "pending_create") {
                const { syncStatus, ...payload } = sess;
                try {
                    await api.post("/inflight/sessions", payload);
                } catch (err) {
                    if (err.response?.status === 409) {
                        toast.error(err.response.data?.message || "Another active session exists on server.");
                        return;
                    }
                    throw err;
                }
                if (sess.status === "closed") await api.patch(`/inflight/sessions/${sess._id}/close`);
                await db.sessions.update(sess._id, { syncStatus: "synced" });
                toast.success("Session synced.");
            } else if (sess.syncStatus === "pending_close") {
                try {
                    await api.patch(`/inflight/sessions/${sess._id}/close`);
                } catch (err) {
                    if (err.response?.status === 404) {
                        await db.sessions.update(sess._id, { syncStatus: "synced" });
                        toast.success("Session synced.");
                        return;
                    }
                    throw err;
                }
                await db.sessions.update(sess._id, { syncStatus: "synced" });
                toast.success("Session synced.");
            } else if (sess.syncStatus === "pending_reopen") {
                await api.patch(`/inflight/sessions/${sess._id}/reopen`);
                await db.sessions.update(sess._id, { syncStatus: "synced" });
                toast.success("Session synced.");
            } else {
                try {
                    const res = await api.get(`/inflight/sessions/${sess._id}`);
                    await db.sessions.put({ ...res.data, syncStatus: "synced" });
                    toast("Session refreshed.");
                } catch (err) {
                    if (err.response?.status === 404) {
                        const { syncStatus, ...payload } = sess;
                        try {
                            await api.post("/inflight/sessions", payload);
                        } catch (postErr) {
                            if (postErr.response?.status === 409) {
                                toast.error(postErr.response.data?.message || "Another active session exists on server.");
                                return;
                            }
                            throw postErr;
                        }
                        if (sess.status === "closed") await api.patch(`/inflight/sessions/${sess._id}/close`);
                        await db.sessions.update(sess._id, { syncStatus: "synced" });
                        toast.success("Session synced.");
                    } else {
                        throw err;
                    }
                }
            }
            await loadSessions();
            const fresh = await db.sessions.get(sess._id);
            if (fresh) setDetailSession(fresh);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Sync failed.");
        } finally {
            setSyncingSession(false);
        }
    }, [detailSession, isOnline, syncingSession, loadSessions]);

    // ── Close session ──
    const handleCloseSession = async () => {
        setSubmitting(true);
        try {
            if (!isOnline || detailSession.syncStatus !== "synced") {
                const pendingStatus = detailSession.syncStatus === "pending_create"
                    ? "pending_create"
                    : "pending_close";
                const updated = {
                    ...detailSession,
                    status: "closed",
                    closedAt: new Date().toISOString(),
                    syncStatus: pendingStatus,
                };
                await db.sessions.put(updated);
                await loadSessions();
                onSessionChange(null);
                setDetailSession(updated);
                toast.success("Session closed offline. Will sync when online.");
                return;
            }
            const res = await api.patch(`/inflight/sessions/${detailSession._id}/close`);
            await db.sessions.put({ ...res.data, syncStatus: "synced" });
            onSessionChange(null);
            setDetailSession(res.data);
            await loadSessions();
            toast.success("Session closed.");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to close session!");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ py: 2 }}>
            {!isOnline && (
                <Alert 
                    severity="warning" 
                    sx={{ alignItems: "center", mb: 2 }}
                >
                    You are offline. Session data may not be up to date.
                </Alert>
            )}

            <ProductCatalog
                cachedCount={cachedCount}
                lastCached={lastCached}
                loading={cacheLoading}
                isOnline={isOnline}
                onDownload={handleDownloadCatalog}
            />
            
            <FlightList
                crewFlights={crewFlights}
                loading={sessionsLoading}
                getFlightSession={getFlightSession}
                onOpenDetail={openDetail}
            />

            <FlightDetailDialog
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                detailFlight={detailFlight}
                detailSession={detailSession}
                isOnline={isOnline}
                syncingSession={syncingSession}
                onSyncSession={handleSyncSession}
                editMode={editMode}
                setEditMode={setEditMode}
                crewEditMode={crewEditMode}
                setCrewEditMode={setCrewEditMode}
                inventoryRows={inventoryRows}
                setInventoryRows={setInventoryRows}
                products={products}
                editRows={editRows}
                setEditRows={setEditRows}
                crewRows={crewRows}
                setCrewRows={setCrewRows}
                availableCrew={availableCrew}
                submitting={submitting}
                onStartSession={handleStartSession}
                onOpenEdit={handleOpenEdit}
                onOpenCrewEdit={handleOpenCrewEdit}
                onSaveInventory={handleSaveInventory}
                onSaveCrew={handleSaveCrew}
                onCloseSession={handleCloseSession}
                onReopenSession={handleReopenSession}
                onUpdateInventoryRow={updateInventoryRow}
                onUpdateEditRow={updateEditRow}
                onUpdateCrewRow={updateCrewRow}
            />
        </Box>
    );
};

export default SessionSetup;
